import { useState } from "react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { destinations, spaceName } from "@/lib/format";
import { VisibilityIcon, visibilityOf } from "./VisibilityIcon";
import { waitForJob } from "./MoveDialog";

const FIXED = "Smart albums and Year Books get their visibility from their photos";

export const AlbumVisibility = ({ album, onChange }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const icon = <VisibilityIcon space={visibilityOf(album.spaces || [album.space])} size={15} testid="album-visibility-icon" />;
  if (!album.can_change_visibility) {
    return <span className="tool-btn" title={album.smart || album.yearbook ? FIXED : undefined} data-testid="album-visibility-fixed">{icon}</span>;
  }
  const pick = async (target) => {
    try {
      const { data: p } = await api.get(`/albums/${album.id}/visibility`, { params: { target } });
      const msg = `Move ${p.move} photo${p.move === 1 ? "" : "s"} to ${spaceName(target)}.${p.stay ? ` ${p.stay} photo${p.stay === 1 ? "" : "s"} owned by others will stay where they are.` : ""}`;
      if (!window.confirm(`${msg}\n\nChange who can see “${album.name}”?`)) return;
      setBusy(true);
      const { data } = await api.post(`/albums/${album.id}/visibility`, { target });
      if (data.job_id) {
        const id = toast.loading(`Moving 0 of ${data.total}…`);
        const fin = await waitForJob(data.job_id, (j) => toast.loading(`Moving ${j.pos} of ${j.total}…`, { id }));
        toast.success(`Moved ${fin.done}${fin.failed ? ` · ${fin.failed} failed` : ""}`, { id });
      } else toast.success(`Now visible to ${spaceName(target)} · moved ${data.done}${data.failed ? ` · ${data.failed} failed` : ""}`);
      onChange(data.album);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const current = album.space === "private" || album.space?.startsWith("user:") ? "private" : album.space;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="tool-btn" disabled={busy} aria-label="Who can see this album" data-testid="album-visibility-button">{icon}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-xs text-neutral-500">Who can see it</DropdownMenuLabel>
        {destinations(user).map(([k, l]) => (
          <DropdownMenuItem key={k} disabled={k === current} onClick={() => pick(k)} data-testid={`album-visibility-${k}`}>
            <VisibilityIcon space={k} size={13} className="mr-2" />{k === "private" ? "Private" : l}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
